import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * Bundle builder — manages the fixed-slot custom bundle in sections/bundle-builder.liquid.
 *
 * State is a flat array of selected products (one entry per filled slot). Clicking a
 * grid "add" button pushes the product into the next free slot; the slot's × button
 * removes it. Add-to-cart posts every selected item to /cart/add.js in ONE request
 * (grouped by variant), each line tagged with a shared `_bundle_id` property, then fires
 * the theme's CartLinesUpdateEvent so the cart drawer refreshes + opens natively — the
 * same flow product-form.js uses.
 */
class BundleBuilder {
  constructor(root) {
    this.root = root;
    this.slotsEl = root.querySelector('[data-bb-slots]');
    this.slotCount = parseInt(this.slotsEl?.dataset.bbSlots ?? '6', 10) || 6;
    this.slotEls = Array.from(root.querySelectorAll('[data-bb-slot]'));
    this.totalEl = root.querySelector('[data-bb-total]');
    this.compareEl = root.querySelector('[data-bb-compare]');
    this.savingsEl = root.querySelector('[data-bb-savings]');
    this.atcBtn = root.querySelector('[data-bb-atc]');
    this.atcText = root.querySelector('[data-bb-atc-text]');

    // Bundle quantity — multiplies the whole assembled bundle (slot_count × qty to cart).
    this.qtyInput = root.querySelector('[data-bb-qty-input]');
    this.qtyDec = root.querySelector('[data-bb-qty-dec]');
    this.qtyInc = root.querySelector('[data-bb-qty-inc]');
    this.bundleQty = 1;
    this.cards = Array.from(root.querySelectorAll('[data-bb-card]'));

    this.currency = root.dataset.currency || 'EUR';
    this.locale = root.dataset.locale || 'de-DE';

    // Bundle discount is DISPLAY-ONLY here: the REAL discount is a native Automatic
    // discount in Admin → Discounts. This % (from the section setting) must match it,
    // so the preview equals what the customer pays at checkout.
    this.discountPercent = Math.max(0, parseFloat(root.dataset.discountPercent) || 0);
    this.savingsLabel = root.dataset.savingsLabel || '';
    // The native Automatic discount kicks in at N+ items — mirror that threshold so
    // the preview only shows the discount once the bundle qualifies.
    this.discountMinQty = Math.max(1, parseInt(root.dataset.discountMinQty, 10) || 1);
    this.badgeText = root.dataset.badgeText || '';
    this.badgeEl = root.querySelector('[data-bb-badge]');
    this.atcLabel = this.atcBtn?.dataset.atcLabel || 'In den Warenkorb';
    this.incompleteLabel = this.atcBtn?.dataset.incompleteLabel || 'Noch [count] auswählen';

    // Subscription is driven by the Seal app widget (an @app block in the section).
    // Seal writes the chosen plan into input[name="selling_plan"]; we read it on
    // render (for the price) and on add-to-cart (to tag every line).
    //
    // variantId -> { ids: [sellingPlanId…], prices: [perDeliveryCents…] } from each
    // product's Seal allocations, so we can map a chosen plan back to its price.
    this.planData = new Map();
    this.cards.forEach((card) => {
      const ids = (card.dataset.sellingPlanIds || '').split(',').filter(Boolean);
      const prices = (card.dataset.sellingPlanPrices || '')
        .split(',')
        .filter(Boolean)
        .map((n) => parseInt(n, 10));
      this.planData.set(card.dataset.variantId, { ids, prices });
    });

    /** @type {{variantId:string, productId:string, title:string, image:string, price:number}[]} */
    this.selection = [];
    this.#restore();

    this.#bind();
    this.render();
  }

  #bind() {
    this.cards.forEach((card) => {
      card.querySelector('[data-bb-add]')?.addEventListener('click', () => this.add(card));
      card.querySelector('[data-bb-inc]')?.addEventListener('click', () => this.add(card));
      card.querySelector('[data-bb-dec]')?.addEventListener('click', () => this.removeOne(card.dataset.variantId));
    });

    // Remove buttons are injected into slots at render time — delegate.
    this.slotsEl?.addEventListener('click', (e) => {
      const remove = e.target.closest('[data-bb-remove]');
      if (remove) this.remove(parseInt(remove.dataset.bbRemove, 10));
    });

    this.atcBtn?.addEventListener('click', () => this.addToCart());

    this.qtyDec?.addEventListener('click', () => this.#setBundleQty(this.bundleQty - 1));
    this.qtyInc?.addEventListener('click', () => this.#setBundleQty(this.bundleQty + 1));
    this.qtyInput?.addEventListener('change', () => this.#setBundleQty(parseInt(this.qtyInput.value, 10)));

    // The Seal widget updates input[name="selling_plan"] asynchronously after its own
    // radios change — re-read on the next tick so the price reflects the selection.
    this.root.addEventListener('change', () => setTimeout(() => this.render(), 60));
  }

  #setBundleQty(n) {
    this.bundleQty = Math.max(1, parseInt(n, 10) || 1);
    if (this.qtyInput) this.qtyInput.value = String(this.bundleQty);
    this.#save();
    this.render();
  }

  /**
   * Read the current choice from the Seal widget.
   *
   * Seal only syncs input[name="selling_plan"] when its widget is inside a product
   * <form>; this section has none, so we read Seal's own controls:
   *   - the checked purchase-type radio (data-type "one_time" vs "subscription"), and
   *   - the frequency <select name="subs_interval"> — its selectedIndex is the frequency.
   *
   * We use the frequency INDEX (not the container's plan id) because each product has
   * its OWN selling-plan id per frequency; every bundle line then gets ITS product's
   * plan at that index. Requires all bundle products (and the container) to share the
   * same Seal group so the frequency order lines up.
   *
   * @returns {{subscribe: boolean, index: number}}
   */
  #subscriptionState() {
    const root = this.root;
    const checked = root.querySelector(
      '.sealsubs-container input.sls-option:checked, input[name^="subs_type_"]:checked'
    );
    if (!checked || checked.dataset.type === 'one_time' || checked.value === 'one_time') {
      return { subscribe: false, index: -1 };
    }
    const scope = checked.closest('.sls-option-container') || root;
    const select = scope.querySelector('select[name="subs_interval"], select.sls-select');
    const index = select ? Math.max(0, select.selectedIndex) : 0;
    return { subscribe: true, index };
  }

  add(card) {
    if (card.hasAttribute('data-unavailable')) return;
    if (this.selection.length >= this.slotCount) return;

    this.selection.push(this.#entryFromCard(card));
    this.#save();
    this.render();
  }

  /** Remove one unit of a variant (from its last-filled slot) — the card's minus button. */
  removeOne(variantId) {
    const idx = this.selection.map((s) => s.variantId).lastIndexOf(String(variantId));
    if (idx !== -1) {
      this.selection.splice(idx, 1);
      this.#save();
      this.render();
    }
  }

  /** Remove the entry at a specific slot index — the slot's × button. */
  remove(index) {
    if (Number.isNaN(index)) return;
    this.selection.splice(index, 1);
    this.#save();
    this.render();
  }

  #entryFromCard(card) {
    return {
      variantId: card.dataset.variantId,
      productId: card.dataset.productId,
      title: card.dataset.title,
      image: card.dataset.image,
      price: parseInt(card.dataset.price, 10) || 0,
    };
  }

  #cardByVariant(variantId) {
    return this.cards.find((c) => c.dataset.variantId === String(variantId));
  }

  #storageKey() {
    return `bundle-builder:${this.root.id}`;
  }

  /** Persist the current selection (variant ids) + bundle qty so they survive a refresh. */
  #save() {
    try {
      localStorage.setItem(
        this.#storageKey(),
        JSON.stringify({ ids: this.selection.map((s) => s.variantId), qty: this.bundleQty })
      );
    } catch (e) {
      /* storage unavailable (private mode / quota) — non-fatal */
    }
  }

  /** Restore a saved selection + qty, rebuilding entries from the current cards so
   *  titles/prices/images stay fresh and stale/removed products are dropped. */
  #restore() {
    let data;
    try {
      data = JSON.parse(localStorage.getItem(this.#storageKey()) || 'null');
    } catch (e) {
      data = null;
    }
    // Support the older array-only format as well as the new { ids, qty } object.
    const ids = Array.isArray(data) ? data : Array.isArray(data?.ids) ? data.ids : [];
    const qty = !Array.isArray(data) && data?.qty ? parseInt(data.qty, 10) : 1;
    this.bundleQty = Math.max(1, qty || 1);
    if (this.qtyInput) this.qtyInput.value = String(this.bundleQty);

    ids.slice(0, this.slotCount).forEach((vid) => {
      const card = this.#cardByVariant(vid);
      if (card && !card.hasAttribute('data-unavailable')) {
        this.selection.push(this.#entryFromCard(card));
      }
    });
  }

  render() {
    // Subscription state drives BOTH the per-slot price and the total: on subscription
    // each product uses its OWN per-delivery price at the chosen frequency index,
    // otherwise its one-time price. Same helper for slot price and total → they match.
    const { subscribe, index } = this.#subscriptionState();
    const priceOf = (item) => {
      const pd = this.planData.get(item.variantId);
      if (subscribe && pd && pd.prices[index] != null) return pd.prices[index];
      return item.price;
    };

    // Slots — each filled slot shows THAT product's own price (works for duplicates:
    // the same product in several slots simply shows its price in each).
    this.slotEls.forEach((slot, i) => {
      const item = this.selection[i];
      if (item) {
        const title = this.#escape(item.title);
        const priceText = this.#formatMoney(priceOf(item));
        slot.classList.add('bb-slot--filled');
        slot.innerHTML =
          `<div class="bb-slot__box">` +
          `<img class="bb-slot__img" src="${item.image}" alt="${title}">` +
          `<button type="button" class="bb-slot__remove" data-bb-remove="${i}" aria-label="Entfernen">&times;</button>` +
          `</div>` +
          `<div class="bb-slot__label">${title}</div>` +
          `<div class="bb-slot__price">${priceText}</div>`;
      } else {
        slot.classList.remove('bb-slot--filled');
        slot.innerHTML = '<div class="bb-slot__box"><span class="bb-slot__plus" aria-hidden="true">+</span></div>';
      }
    });

    // Gross total (sum of selected × bundle qty, in cents) — same per-item price as slots.
    const grossCents = this.selection.reduce((sum, item) => sum + priceOf(item), 0) * this.bundleQty;

    // Bundle discount (DISPLAY): applies only once the bundle reaches the quantity
    // threshold of the native Automatic discount (e.g. 5+ items). net = what the
    // customer pays; matches checkout as long as % and threshold equal that discount.
    // Preview the discount from the FIRST item: the buy button stays disabled until the
    // bundle is complete, and a complete bundle (all slots × qty) always meets the
    // discount's quantity threshold — so there's no point waiting to hit it mid-build.
    // Guard on the FULL bundle qualifying, not the current count, to stay correct if
    // slot_count were ever below the threshold.
    const fullBundleQualifies = this.slotCount * this.bundleQty >= this.discountMinQty;
    const qualifies = this.discountPercent > 0 && this.selection.length > 0 && fullBundleQualifies;
    // Match Shopify's rounding EXACTLY: it applies the % PER UNIT and floors each unit's
    // discount to the cent (4.99 × 10% = 0.499 → €0.49/unit), it does NOT round the whole
    // total once. Summing per-unit floors keeps the preview equal to the checkout total.
    const discountCents = qualifies
      ? this.selection.reduce(
          (sum, item) => sum + Math.floor((priceOf(item) * this.discountPercent) / 100),
          0,
        ) * this.bundleQty
      : 0;
    const netCents = grossCents - discountCents;

    // Main total = net (discounted). Also used on the ATC button below.
    const formattedTotal = this.#formatMoney(netCents);
    if (this.totalEl) this.totalEl.textContent = formattedTotal;

    // Original (strikethrough) + savings — shown only when a discount actually applies.
    if (this.compareEl) {
      this.compareEl.hidden = discountCents <= 0;
      if (discountCents > 0) this.compareEl.textContent = this.#formatMoney(grossCents);
    }
    if (this.savingsEl) {
      this.savingsEl.hidden = discountCents <= 0;
      if (discountCents > 0) {
        this.savingsEl.textContent = this.savingsLabel.replace('[amount]', this.#formatMoney(discountCents));
      }
    }

    // Advertising badge (e.g. "Spare 10% im Bundle") — shown whenever a discount is
    // configured, regardless of the current selection, so it advertises the offer.
    if (this.badgeEl) {
      const showBadge = this.discountPercent > 0 && this.badgeText.trim() !== '';
      this.badgeEl.hidden = !showBadge;
      if (showBadge) {
        this.badgeEl.textContent = this.badgeText.replace('[percent]', String(this.discountPercent));
      }
    }

    // Keep the qty control in sync (e.g. after restore) and block going below 1.
    if (this.qtyInput) this.qtyInput.value = String(this.bundleQty);
    if (this.qtyDec) this.qtyDec.disabled = this.bundleQty <= 1;

    // Per-card control: show the "add" button at count 0, else the quantity stepper.
    const isFull = this.selection.length >= this.slotCount;
    this.cards.forEach((card) => {
      const unavailable = card.hasAttribute('data-unavailable');
      const count = this.selection.filter((s) => s.variantId === card.dataset.variantId).length;
      const addBtn = card.querySelector('[data-bb-add]');
      const qty = card.querySelector('[data-bb-qty]');
      const countEl = card.querySelector('[data-bb-count]');
      const incBtn = card.querySelector('[data-bb-inc]');

      if (count > 0) {
        if (addBtn) addBtn.hidden = true;
        if (qty) qty.hidden = false;
        if (countEl) countEl.textContent = String(count);
        if (incBtn) incBtn.disabled = isFull;
      } else {
        if (addBtn) {
          addBtn.hidden = false;
          if (!unavailable) addBtn.disabled = isFull;
        }
        if (qty) qty.hidden = true;
      }
    });

    // Add-to-cart enabled only when every slot is filled
    const remaining = this.slotCount - this.selection.length;
    if (!this.atcBtn) return;
    if (remaining <= 0) {
      this.atcBtn.disabled = false;
      this.atcBtn.classList.remove('bb-atc--incomplete');
      // Show the total (with the chosen selling plan) on the button, e.g. "€49,90 – In den Warenkorb".
      if (this.atcText) this.atcText.textContent = `${formattedTotal} – ${this.atcLabel}`;
    } else {
      this.atcBtn.disabled = true;
      this.atcBtn.classList.add('bb-atc--incomplete');
      if (this.atcText) this.atcText.textContent = this.incompleteLabel.replace('[count]', String(remaining));
    }
  }

  /** Group the flat selection into cart line items (one per variant, with quantity). */
  #buildItems(bundleId) {
    const { subscribe, index } = this.#subscriptionState();
    const counts = new Map();
    this.selection.forEach((item) => {
      counts.set(item.variantId, (counts.get(item.variantId) || 0) + 1);
    });
    return Array.from(counts.entries()).map(([id, perBundleQty]) => {
      const quantity = perBundleQty * this.bundleQty;
      const line = { id: Number(id), quantity, properties: { _bundle_id: bundleId } };
      // Each line uses ITS product's own plan id at the chosen frequency index. A box
      // with no plan at that index is added one-time instead of erroring.
      if (subscribe) {
        const planId = this.planData.get(id)?.ids?.[index];
        if (planId) line.selling_plan = Number(planId);
      }
      return line;
    });
  }

  async addToCart() {
    if (this.selection.length < this.slotCount) return;

    // `Theme` is a lexical global from a classic <script> (snippets/scripts.liquid),
    // so it is NOT a property of window — read the bare binding with a safe fallback.
    const routes = (typeof Theme !== 'undefined' && Theme.routes) || {};
    const cartAddUrl = routes.cart_add_url || '/cart/add.js';
    const cartUrl = (routes.cart_url || '/cart') + '.js';

    const bundleId = this.#guid();
    const items = this.#buildItems(bundleId);
    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
      .filter(Boolean);

    // Notify the theme so the cart drawer auto-opens + re-renders (same flow as
    // product-form.js). Guarded so a change in the event API never blocks the add.
    let deferred = null;
    try {
      deferred = CartLinesUpdateEvent.createPromise();
      document.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: items.map((it) => ({ merchandiseId: String(it.id), quantity: it.quantity })),
          promise: deferred.promise,
        })
      );
    } catch (e) {
      console.warn('[bundle-builder] CartLinesUpdateEvent unavailable', e);
    }

    this.atcBtn.classList.add('bb-atc--loading');

    try {
      const addResponse = await fetch(cartAddUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/html',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          items,
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      }).then((r) => r.json());

      if (addResponse.status) {
        // Shopify returns a `status` field only on error.
        console.error('[bundle-builder] add to cart error', addResponse);
        deferred?.resolve({ cart: null, detail: { didError: true } });
        return;
      }

      const ajaxCart = await fetch(cartUrl).then((r) => r.json());

      deferred?.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          items: ajaxCart.items,
          source: 'bundle-builder',
          sourceId: this.root.id,
          itemCount: this.selection.length,
          sections: addResponse.sections,
          didError: false,
        },
      });

      // Keep the assembled bundle in place after adding (persists across refresh too).
    } catch (error) {
      console.error('[bundle-builder] add to cart failed', error);
      deferred?.resolve({ cart: null, detail: { didError: true } });
    } finally {
      this.atcBtn.classList.remove('bb-atc--loading');
    }
  }

  #formatMoney(cents) {
    try {
      return new Intl.NumberFormat(this.locale, { style: 'currency', currency: this.currency }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(2);
    }
  }

  #escape(str) {
    return String(str ?? '').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  #guid() {
    let d = new Date().getTime();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (d + Math.random() * 16) % 16 | 0;
      d = Math.floor(d / 16);
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
  }
}

function initBundleBuilders() {
  document.querySelectorAll('[data-bundle-builder]').forEach((el) => {
    if (!el.__bundleBuilder) el.__bundleBuilder = new BundleBuilder(el);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initBundleBuilders);
} else {
  initBundleBuilders();
}

// Re-init when the section is re-rendered in the theme editor.
document.addEventListener('shopify:section:load', initBundleBuilders);
